import { Component, ErrorInfo, ReactNode } from 'react';
import { usePageLoadStatus } from 'src/hooks/usePageLoadStatus';

interface PageLoadErrorBoundaryProps {
  componentName: string;
  children: ReactNode;
}

interface PageLoadErrorBoundaryState {
  hasError: boolean;
}

// HOC to inject page load status context into class component
function withPageLoadStatus<P extends object>(
  WrappedComponent: React.ComponentType<
    P & { pageLoadStatus: ReturnType<typeof usePageLoadStatus> }
  >
) {
  return function WithPageLoadStatusComponent(props: P) {
    const pageLoadStatus = usePageLoadStatus();
    return <WrappedComponent {...props} pageLoadStatus={pageLoadStatus} />;
  };
}

class PageLoadErrorBoundaryImpl extends Component<
  PageLoadErrorBoundaryProps & {
    pageLoadStatus: ReturnType<typeof usePageLoadStatus>;
  },
  PageLoadErrorBoundaryState
> {
  constructor(
    props: PageLoadErrorBoundaryProps & {
      pageLoadStatus: ReturnType<typeof usePageLoadStatus>;
    }
  ) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(_: Error): PageLoadErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    const { componentName, pageLoadStatus } = this.props;
    pageLoadStatus.markComponentError(componentName, error);

    // Log error details for debugging
    console.error(
      `Component ${componentName} failed to load:`,
      error,
      errorInfo
    );
  }

  render() {
    // Always render children - we just want to track the error, not prevent rendering
    return this.props.children;
  }
}

export const PageLoadErrorBoundary = withPageLoadStatus(
  PageLoadErrorBoundaryImpl
);
